import { useContext, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { TSigninReturn, TUpdateUser, TUser } from '../types/users.types';

import { AuthContext } from '@modules/users';

import { ApiContext } from '@shared/contexts';
import { TMap } from '@shared/types';

const sortUsers = (users: readonly TUser[]) => [...users].sort((a, b) => a.mail.localeCompare(b.mail));
export const USERS_QUERY_KEY = ['users'] as const;
export const getSignInQueryKey = (homeAccountId: string) => [`sign-in-${homeAccountId}`] as const;

export const useSignIn = () => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);
  const { account } = useContext(AuthContext);

  const signIn = (): Promise<TSigninReturn> => apiClient.get(`/users/signin`).then((res) => res.data);

  const { data, isError, error } = useQuery({
    retry,
    queryKey: getSignInQueryKey(account.homeAccountId), // homeAccountId is the unique identifier across all MS services
    queryFn: signIn,
  });
  useHandleQueryError(isError, error);

  return { error, data };
};

export const useUsers = () => {
  const { apiClient, useHandleQueryError } = useContext(ApiContext);

  const getUsers = (): Promise<TUser[]> => apiClient.get(`/users`).then((res) => res.data);
  const { data, isError, error } = useQuery({
    queryKey: USERS_QUERY_KEY,
    queryFn: getUsers,
    refetchOnMount: false,
  });
  useHandleQueryError(isError, error);

  const [users, userMap] = useMemo(() => {
    if (!data) return [undefined, undefined];

    const userMap: TMap<string, TUser> = {};
    data.forEach((user: TUser) => {
      userMap[user._id] = user;
    });

    return [sortUsers(data), userMap];
  }, [data]);

  return {
    users,
    userMap,
    error,
  };
};

export const useCreate = () => {
  const { apiClient, handleApiError } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: { mail: string }): Promise<TUser> => {
    const res = await apiClient.post(`/users`, variables);
    return res.data;
  };

  return useMutation({
    mutationFn,
    onSuccess: (user: TUser) => {
      queryClient.setQueryData(USERS_QUERY_KEY, (oldData: TUser[] | undefined) =>
        oldData ? sortUsers([user, ...oldData]) : [user]
      );
    },
    onError: handleApiError('Updating user failed!'),
  });
};

export const useLog = () => {
  const { apiClient } = useContext(ApiContext);

  const mutationFn = async (variables: { message: string }): Promise<TUser> => apiClient.post(`/users/log`, variables);

  return useMutation({
    mutationFn,
    // no handleError
  });
};

export const useUpdate = () => {
  const { apiClient, handleApiError } = useContext(ApiContext);
  const { account } = useContext(AuthContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: { id: string; update: TUpdateUser }): Promise<TUser> => {
    const res = await apiClient.put(`/users/${variables.id}`, variables.update);
    return res.data;
  };

  return useMutation({
    mutationFn,
    onSuccess: (user: TUser) => {
      queryClient.setQueryData(USERS_QUERY_KEY, (oldData: TUser[] | undefined) =>
        oldData ? sortUsers([user, ...oldData.filter((userIter) => userIter._id !== user._id)]) : [user]
      );
      queryClient.setQueryData(getSignInQueryKey(account.homeAccountId), (oldData: TSigninReturn | undefined) =>
        oldData?.user._id === user._id ? { ...oldData, user } : oldData
      );
    },
    onError: handleApiError('Updating user failed!'),
  });
};

export const useDeactivate = () => {
  const { apiClient, handleApiError } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: { id: string }): Promise<string> => {
    await apiClient.delete(`/users/${variables.id}`);
    return variables.id;
  };

  return useMutation({
    mutationFn,
    onSuccess: (id: string) => {
      queryClient.setQueryData(USERS_QUERY_KEY, (oldData: TUser[] | undefined) =>
        oldData ? oldData.filter((userIter) => userIter._id !== id) : []
      );
    },
    onError: handleApiError('Updating user failed!'),
  });
};
